<template>
  <div class="d-flex ml-2 mb-2">
    <div class="row align-items-center">
      <div class="color-chart-box bg-non-business-color"></div>
      <span>
        {{ $t('models/conciergeAdviserSchedulePeriods.schedule.legend.dealership_closed') }}
      </span>
    </div>
    <div class="row align-items-center">
      <div class="color-chart-box bg-green"></div>
      <span>
        {{ $t('models/conciergeAdviserSchedulePeriods.schedule.legend.adviser_available') }}
      </span>
    </div>
  </div>
  <FullCalendar
    :key="'calendar-' + schedulePeriod.id"
    :options="calendarOptions"
    ref="fullCalendar"
  >
    <template v-slot:eventContent="event">
      <div class="w-100 d-flex justify-content-between align-items-center">
        <div>
          <span>{{ event.timeText }}</span>
        </div>
        <i class="fa fa-x mx-1"></i>
      </div>
    </template>
  </FullCalendar>
  <DeleteModal
    v-if="showDeleteModal"
    :key="'delete-modal-' + schedulePeriod.id"
    @hideModal="hideModal"
    @deleteRecord="deleteRecord"
  >
    <template #title>
      {{ $t('models/conciergeAdviserSchedulePeriods.schedule.modals.delete_time_slot') }}
    </template>

    <template #content>
      {{
        $t('models/conciergeAdviserSchedulePeriods.schedule.modals.delete_time_slot_confirmation')
      }}
    </template>
  </DeleteModal>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import {
  type CalendarApi,
  type CalendarOptions,
  type DateSelectArg,
  type EventApi,
  type EventClickArg
} from '@fullcalendar/core'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import bootstrapPlugin from '@fullcalendar/bootstrap'
import frLocale from '@fullcalendar/core/locales/fr'
import DeleteModal from '@components/modals/DeleteModal.vue'
import type { ConciergeAdviserSchedulePeriodData } from '~types/concierge-admin'

type FullCalendarInstance = {
  getApi: () => CalendarApi
}

export default defineComponent({
  name: 'ScheduleCalendar',
  components: {
    FullCalendar,
    DeleteModal
  },
  emits: ['update', 'uneditable'],
  props: {
    schedulePeriod: {
      type: Object as PropType<ConciergeAdviserSchedulePeriodData>,
      required: true
    },
    businessHours: {
      type: Array,
      required: true
    },
    weekLength: {
      type: Number,
      required: true
    },
    isEditable: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      fullCalendar: null as FullCalendarInstance | null,
      showDeleteModal: false,
      selectedEvent: null as EventClickArg | null
    }
  },
  computed: {
    calendarOptions(): CalendarOptions {
      return {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, bootstrapPlugin],
        headerToolbar: {
          left: '',
          center: '',
          right: ''
        },
        locale: frLocale,
        businessHours: this.businessHours as CalendarOptions['businessHours'],
        duration: { days: this.weekLength },
        dayHeaderFormat: { weekday: 'long' },
        initialView: 'timeGridWeek',
        initialEvents: this.initialEvents,
        editable: true,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: this.weekLength > 5,
        slotMinTime: '05:00:00',
        slotMaxTime: '23:30:00',
        expandRows: true,
        select: this.handleDateSelect as (arg: DateSelectArg) => void,
        eventClick: this.handleEventClick as (arg: EventClickArg) => void,
        eventsSet: this.handleEvents as (events: EventApi[]) => void
      }
    },
    initialEvents() {
      return this.schedulePeriod.availabilities
    }
  },
  methods: {
    calendarApi(): CalendarApi | null {
      return (this.fullCalendar as FullCalendarInstance)?.getApi() ?? null
    },
    deleteRecord(): void {
      ;(this.selectedEvent as any)?.event.remove()
      this.showDeleteModal = false
    },
    hideModal(): void {
      this.showDeleteModal = false
    },
    handleDateSelect(selectInfo: DateSelectArg): void {
      const api = selectInfo.view.calendar

      api.unselect()
      api.addEvent({
        id: this.createEventId(),
        start: selectInfo.startStr,
        end: selectInfo.endStr,
        allDay: selectInfo.allDay
      })
    },
    handleEventClick(clickInfo: EventClickArg): void {
      if (this.$can('edit concierge advisers schedules')) {
        this.selectedEvent = clickInfo
        this.showDeleteModal = true
        return
      }

      alert("Vous n'avez pas les permissions nécessaires, veuillez contacter votre gestionnaire")
    },
    handleEvents(events: EventApi[]): void {
      if (!this.$can('edit concierge advisers schedules')) {
        alert("Vous n'avez pas les permissions nécessaires, veuillez contacter votre gestionnaire")
        return
      }

      if (!this.isEditable) {
        this.$emit('uneditable')
        return
      }

      const availabilities = events.flatMap(event => {
        if (!event.start || !event.end) {
          return []
        }

        return [
          {
            start_time: this.formatTime(event.start.getHours(), event.start.getMinutes()),
            end_time: this.formatTime(event.end.getHours(), event.end.getMinutes()),
            weekday: event.start.getDay()
          }
        ]
      })

      this.$emit('update', availabilities)
    },
    createEventId(): string {
      return String(new Date().toISOString().replace(/T.*$/, ''))
    },
    formatTime(hours: number, minutes: number): string {
      return `${this.padNumber(hours)}:${this.padNumber(minutes)}`
    },
    padNumber(value: number): string {
      return value.toLocaleString('fr-CA', {
        minimumIntegerDigits: 2,
        useGrouping: false
      })
    }
  }
})
</script>
<style>
.fc .fc-daygrid-day.fc-day-today,
.fc .fc-timegrid-col.fc-day-today {
  background-color: inherit !important;
}

.fc .fc-toolbar.fc-header-toolbar {
  margin: 0;
  padding: 0;
}

.color-chart-box {
  width: 34px;
  height: 19px;
  border-radius: 0.25rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.color-chart-box + span {
  all: unset;
  margin-right: 3em;
  margin-left: 0.5em;
  font-weight: 600;
  color: #6c757d;
}

.bg-green {
  background-color: #4e9a60;
}

.bg-non-business-color {
  background-color: hsla(0, 0%, 84%, 0.3);
}

.fc-event-main {
  background-color: #4e9a60;
  outline-color: #4e9a60;
}

.fc-v-event {
  border-color: #4e9a60 !important;
}
</style>
